import { useState } from 'react';
import { Button } from '@/components/ui/button-comp';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCookie } from '@ekonavi/auth';
import { CheckCircle, XCircle, Clock, Eye, Award, Shield, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiEndpoint } from '@/config';

interface RoleRequest {
	id: string;
	user_id: string;
	role_type: string;
	status: string;
	created_at: string;
	experience_description: string;
	certifications?: string;
	user?: {
		name: string;
		email: string;
	};
}

interface RoleRequestApprovalProps {
	roleRequests: RoleRequest[];
	onUpdate: () => void;
}

export function RoleRequestApproval({ roleRequests, onUpdate }: RoleRequestApprovalProps) {
	const authToken = getCookie('auth_token');
	const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : undefined;
	const [isLoading, setIsLoading] = useState(false);
	const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
	const [adminNotes, setAdminNotes] = useState<{ [key: string]: string }>({});

	const handleApprove = async (requestId: string, roleType: string) => {
		if (!authHeaders) return;

		setIsLoading(true);
		try {
			// Update role request status
			const updateResponse = await fetch(`${apiEndpoint}/role-requests/${requestId}`, {
				method: 'PUT',
				headers: {
					...authHeaders,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					status: 'approved',
					admin_notes: adminNotes[requestId] || 'Aprovado pelo administrador'
				})
			});

			if (updateResponse.ok) {
				// Create the appropriate role profile
				const roleRequest = roleRequests.find(r => r.id === requestId);
				if (roleRequest) {
					let roleEndpoint = '';
					switch (roleType) {
						case 'verifier':
							roleEndpoint = '/v1/verifiers';
							break;
						case 'valuator':
							roleEndpoint = '/v1/valuators';
							break;
						case 'certifier':
							roleEndpoint = '/v1/certifiers';
							break;
					}

					if (roleEndpoint) {
						const createRoleResponse = await fetch(`${apiEndpoint}${roleEndpoint.replace('/v1', '')}`, {
							method: 'POST',
							headers: {
								...authHeaders,
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({
								user_id: roleRequest.user_id,
								experience: roleRequest.experience_description,
								certifications: roleRequest.certifications || '',
								status: 'active'
							})
						});

						if (createRoleResponse.ok) {
							toast.success(`Solicitação aprovada e perfil de ${getRoleLabel(roleType)} criado!`);
							onUpdate();
						} else {
							toast.error('Erro ao criar perfil de função');
						}
					}
				}
			} else {
				toast.error('Erro ao aprovar solicitação');
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error approving role request:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleReject = async (requestId: string) => {
		if (!authHeaders) return;

		setIsLoading(true);
		try {
			const response = await fetch(`${apiEndpoint}/role-requests/${requestId}`, {
				method: 'PUT',
				headers: {
					...authHeaders,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					status: 'rejected',
					admin_notes: adminNotes[requestId] || 'Rejeitado pelo administrador'
				})
			});

			if (response.ok) {
				toast.success('Solicitação rejeitada');
				onUpdate();
			} else {
				toast.error('Erro ao rejeitar solicitação');
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error rejecting role request:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const getRoleIcon = (roleType: string) => {
		switch (roleType) {
			case 'verifier': return <Eye className="w-4 h-4" />;
			case 'valuator': return <Award className="w-4 h-4" />;
			case 'certifier': return <Shield className="w-4 h-4" />;
			default: return <Users className="w-4 h-4" />;
		}
	};

	const getRoleLabel = (roleType: string) => {
		switch (roleType) {
			case 'verifier': return 'Verificador';
			case 'valuator': return 'Avaliador';
			case 'certifier': return 'Certificador';
			default: return roleType;
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case 'pending':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
			case 'approved':
				return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
			case 'rejected':
				return <Badge variant="outline" className="text-red-600 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	if (roleRequests.length === 0) {
		return (
			<div className="text-center py-8">
				<Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
				<p className="text-muted-foreground">Nenhuma solicitação de função encontrada</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Usuário</TableHead>
						<TableHead>Função</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Data</TableHead>
						<TableHead>Ações</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{roleRequests.map((request) => (
						<>
							<TableRow key={request.id}>
								<TableCell>
									<div>
										<p className="font-medium">{request.user?.name || 'Nome não informado'}</p>
										<p className="text-sm text-muted-foreground">{request.user?.email || request.user_id}</p>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										{getRoleIcon(request.role_type)}
										{getRoleLabel(request.role_type)}
									</div>
								</TableCell>
								<TableCell>{getStatusBadge(request.status)}</TableCell>
								<TableCell>
									{new Date(request.created_at).toLocaleDateString('pt-BR')}
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setExpandedRequest(
												expandedRequest === request.id ? null : request.id
											)}
										>
											{expandedRequest === request.id ? 'Ocultar' : 'Detalhes'}
										</Button>
										{request.status === 'pending' && (
											<>
												<Button
													variant="outline"
													size="sm"
													className="text-green-600 border-green-300 hover:bg-green-50"
													onClick={() => handleApprove(request.id, request.role_type)}
													disabled={isLoading}
												>
													<CheckCircle className="w-4 h-4 mr-1" />
													Aprovar
												</Button>
												<Button
													variant="outline"
													size="sm"
													className="text-red-600 border-red-300 hover:bg-red-50"
													onClick={() => handleReject(request.id)}
													disabled={isLoading}
												>
													<XCircle className="w-4 h-4 mr-1" />
													Rejeitar
												</Button>
											</>
										)}
									</div>
								</TableCell>
							</TableRow>
							{expandedRequest === request.id && (
								<TableRow>
									<TableCell colSpan={5}>
										<div className="p-4 bg-muted/30 rounded-lg space-y-4">
											<div>
												<h4 className="font-medium mb-2">Experiência Profissional</h4>
												<p className="text-sm text-muted-foreground">
													{request.experience_description}
												</p>
											</div>
											{request.certifications && (
												<div>
													<h4 className="font-medium mb-2">Certificações</h4>
													<p className="text-sm text-muted-foreground">
														{request.certifications}
													</p>
												</div>
											)}
											{request.status === 'pending' && (
												<div className="space-y-2">
													<Label htmlFor={`notes-${request.id}`}>Observações do Administrador</Label>
													<Input
														id={`notes-${request.id}`}
														value={adminNotes[request.id] || ''}
														onChange={(e) => setAdminNotes(prev => ({
															...prev,
															[request.id]: e.target.value
														}))}
														placeholder="Adicione observações sobre a decisão..."
													/>
												</div>
											)}
										</div>
									</TableCell>
								</TableRow>
							)}
						</>
					))}
				</TableBody>
			</Table>
		</div>
	);
}